import { useEffect } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, hasAdminRole, hasSuperAdminRole } from "@/context/AuthContext";

// Route guards for admin-only and super_admin-only routes.
//
// Behaviour:
// - While the auth context is loading (initial /auth/me roundtrip),
//   render a small inline spinner. We do NOT redirect during loading,
//   because hard-refreshing an admin page would otherwise bounce a real
//   admin out before the user object is available.
// - Not logged in → redirect to /login?redirect=<current path>. After
//   the magic-link round-trip, /auth/verify will land them back here.
// - Logged in but missing the required role → redirect to / and surface
//   a toast. We don't render a 403 page because regular users hitting
//   /admin/* by accident is a low-stakes UX, not a security issue —
//   the backend already enforces the real check.

function LoadingShim() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#1B4D3E",
      }}
    >
      Loading…
    </div>
  );
}

// Recognise the one /admin/* route that has a public twin. When an admin
// shares /admin/events/:id by mistake, a non-admin who follows the link is
// silently rerouted to /events/:id instead of bouncing to home with a
// confusing "Admin access required" toast. useParams() can't see :eventId
// from this guard's render context (it's bound to the child route), so we
// match the pathname directly.
const ADMIN_EVENT_PATH = /^\/admin\/events\/([^/]+)\/?$/;

export function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const denied = !loading && user && !hasAdminRole(user);
  const eventsAdminMatch = denied ? location.pathname.match(ADMIN_EVENT_PATH) : null;
  const silentEventRedirect = !!eventsAdminMatch;

  // Surface the toast in an effect so it doesn't fire during render and
  // doesn't double-fire on StrictMode mount. Suppressed for the events
  // redirect path — the user thinks they're going to an event page, so
  // silently delivering them there is less jarring than a denial toast.
  useEffect(() => {
    if (denied && !silentEventRedirect) toast.error("Admin access required");
  }, [denied, silentEventRedirect]);

  if (loading) return <LoadingShim />;
  if (!user) {
    // LoginPage reads redirectPath from location.state.from (existing
    // convention) and forwards it to /auth/start so the magic-link URL
    // lands the user back here after verify.
    const from = location.pathname + location.search;
    return <Navigate to="/login" state={{ from }} replace />;
  }
  if (eventsAdminMatch) {
    return <Navigate to={`/events/${eventsAdminMatch[1]}`} replace />;
  }
  if (denied) return <Navigate to="/" replace />;
  return children;
}

export function RequireSuperAdmin({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const denied = !loading && user && !hasSuperAdminRole(user);

  useEffect(() => {
    if (denied) toast.error("Super-admin access required");
  }, [denied]);

  if (loading) return <LoadingShim />;
  if (!user) {
    const from = location.pathname + location.search;
    return <Navigate to="/login" state={{ from }} replace />;
  }
  if (denied) return <Navigate to="/admin/dashboard" replace />;
  return children;
}
